import { DocumentRenderData, RequiredField } from '../types'
import {
  MergeField,
  CollapsibleClause,
  SignatureBlock,
  IntermediaryBlock,
  blank,
  formatDate,
} from './shared'

export const assignmentOfPurchaseContractRequiredFields: RequiredField[] = [
  { key: 'exchanger.names', description: 'Exchanger full name', sourceHub: 'purchase', sourceStep: 'Exchanger Info' },
  { key: 'property.addressOrLegalDescription', description: 'Replacement property address', sourceHub: 'purchase', sourceStep: 'Replacement Property' },
]

interface Props {
  data: DocumentRenderData
}

export function AssignmentOfPurchaseContractTemplate({ data }: Props) {
  const { exchanger, property, signature, bundleMeta } = data
  const exchangerLegal = exchanger.legalEntityDescription || exchanger.names
  const exchangeAgreementDate = formatDate(bundleMeta?.exchangeAgreementDate)
  const purchaseAgreementDate = formatDate(property?.purchaseAgreementDate)
  const effectiveDate = exchangeAgreementDate
    || (signature?.capturedAt ? new Date(signature.capturedAt).toLocaleDateString() : '')
    || new Date().toLocaleDateString()

  return (
    <div className="space-y-6 text-sm text-foreground/85 font-serif">
      <div className="text-center">
        <h4 className="text-xl font-bold tracking-wide text-foreground">REPLACEMENT PROPERTY ASSIGNMENT</h4>
        <p className="text-xs text-muted-foreground/80 mt-1 italic">
          Phase Two — Closing Bundle (Pursuant to Treas. Reg. §1.1031(k)-1(g)(4)(v))
        </p>
      </div>

      <div className="rounded-md border border-border bg-card/50 p-3 space-y-1 text-xs">
        <MergeField label="Re: Exchange File No." value={bundleMeta?.open1031FileNumber} width={28} />
        <MergeField label="Replacement Property:" value={property?.addressOrLegalDescription} width={50} />
        <MergeField label="Exchanger:" value={exchangerLegal} width={40} />
        <MergeField label="Effective Date:" value={effectiveDate} width={20} />
      </div>

      <section className="space-y-2">
        <h5 className="font-semibold text-sm">RECITALS</h5>
        <p className="text-xs leading-relaxed">
          <strong>A.</strong> Qualified Intermediary and Exchanger are parties to that certain
          Exchange Agreement dated as of{' '}
          <span className="font-mono">{blank(exchangeAgreementDate, 20)}</span> (the &ldquo;Exchange
          Agreement&rdquo;) pursuant to which Intermediary has agreed to act as a &ldquo;qualified
          intermediary&rdquo; within the meaning of Treasury Regulations Section 1.1031(k)-1(g)(4) in
          order to engage with Exchanger in a like-kind exchange of real properties under Section
          1031 of the Code.
        </p>
        <p className="text-xs leading-relaxed">
          <strong>B.</strong> Exchanger has entered into a purchase and sale agreement, or similarly
          titled contract dated{' '}
          <span className="font-mono">{blank(purchaseAgreementDate, 20)}</span>{' '}
          (the &ldquo;Replacement Property Purchase Agreement&rdquo;) with the Seller named therein,
          pursuant to which the Seller has agreed to transfer the Replacement Property to Exchanger.
        </p>
        <p className="text-xs leading-relaxed">
          <strong>C.</strong> As set forth in the Exchange Agreement, Exchanger desires to assign to
          Intermediary its Rights (but not its duties and obligations) under the Replacement Property
          Purchase Agreement, and Qualified Intermediary desires to accept the same, pursuant to a
          form of assignment which meets the requirements of Treasury Regulations Section
          1.1031(k)-1(g)(4)(v).
        </p>
      </section>

      <section className="space-y-2">
        <h5 className="font-semibold text-sm">AGREEMENTS</h5>
        <div className="space-y-2 text-xs leading-relaxed">
          <p>
            <strong>1. Assignment of Rights.</strong> Exchanger hereby assigns to Intermediary
            Exchanger&rsquo;s rights under the Replacement Property Purchase Agreement with respect
            to the acquisition of the Replacement Property from the Seller. Concurrently, Exchanger
            hereby assigns to Intermediary Exchanger&rsquo;s obligation under the Replacement
            Property Purchase Agreement to pay to the Seller the purchase price for the Replacement
            Property.
          </p>
          <p>
            <strong>2. Acceptance and Agreement to Acquire and Transfer.</strong> Intermediary hereby
            accepts the foregoing assignment from Exchanger and agrees to pay the Purchase Price to
            the Seller, limited to the amount of funds presently held in the Exchange Account
            established pursuant to the Exchange Agreement.
          </p>
          <p>
            <strong>3. Conveyance.</strong> In order to facilitate efficient conveyance of title to
            the Replacement Property, the Seller shall be directed to convey title to the Replacement
            Property directly to Exchanger pursuant to one or more instruments of transfer which
            comply with the terms of the Replacement Property Purchase Agreement. The Qualified
            Intermediary will NOT take title to the Replacement Property.
          </p>
          <p>
            <strong>4. No Impairment.</strong> Nothing contained in this Assignment shall be deemed
            to impair any rights of the Seller under the Replacement Property Purchase Agreement, and
            the Seller shall retain any and all rights and remedies against Exchanger thereunder.
          </p>
          <p>
            <strong>5. Non-Real Property.</strong> Exchanger shall not assign to Intermediary, and
            Intermediary shall not assume, any right to acquire property which does not constitute
            &ldquo;real property&rdquo; under Treasury Regulations Section 1.1031(a)-3.
          </p>
          <p>
            <strong>6. Counterparts and Signatures.</strong> This Assignment may be executed in any
            number of counterparts. A facsimile, scanned, electronic, or digital signature shall be
            deemed to be an original &ldquo;wet ink&rdquo; signature.
          </p>
        </div>

        <CollapsibleClause title="View full legal text from lawyer-drafted .docx">
          <p>
            <strong>Section 4 (full).</strong> If, prior to the Replacement Property Closing,
            Exchanger breaches any of its obligations under the Replacement Property Purchase
            Agreement, the Exchange Agreement or this Assignment, Intermediary may terminate this
            Assignment by written notice to Exchanger. All covenants, indemnities, representations,
            warranties, and post-closing obligations under the Replacement Property Purchase
            Agreement running from Exchanger to the Buyer shall continue to run directly from
            Exchanger to the Buyer.
          </p>
          <p>
            <strong>Section 2 (full).</strong> To the extent the Purchase Price exceeds the funds
            presently held in the Exchange Account, Exchanger shall either (i) engage in a Seller
            Debt Assumption or (ii) deposit with Intermediary (or, if Intermediary so directs, pay
            directly to the Seller) cash in an amount equal to such difference to permit Intermediary
            to pay the balance of the Purchase Price.
          </p>
        </CollapsibleClause>
      </section>

      <section className="text-xs italic text-muted-foreground/90">
        <p>
          IN WITNESS WHEREOF, the parties have executed and delivered this Replacement Property
          Assignment as of the date first written above.
        </p>
      </section>

      <section className="border-t-2 border-border pt-4 space-y-4">
        <SignatureBlock
          label="EXCHANGER SIGNATURE:"
          signerName={exchangerLegal}
          signature={signature}
        />
        <div className="pt-2">
          <IntermediaryBlock data={data} />
        </div>
      </section>
    </div>
  )
}
